import { useStorage } from "@vueuse/core";
import { computed, watch } from "vue";

export type Theme = "dark" | "light";

const STORAGE_KEY = "n7ait-theme";

/**
 * The active colour theme, persisted per browser. Module scope, so every caller shares one
 * value and the toggle need not be passed around. Applying it is one attribute on `<html>`:
 * `tokens.css` hangs the light palette off `[data-theme="light"]`, and Tailwind and the
 * PrimeVue preset read the same variables, so nothing else has to know a theme exists.
 *
 * Dark is the default — what the app shipped with, and what renders if storage is unreadable.
 */
const theme = useStorage<Theme>(STORAGE_KEY, "dark");

const apply = (value: Theme) => document.documentElement.setAttribute("data-theme", value);

watch(theme, apply);

export function useTheme() {
  return {
    theme,
    isDark: computed(() => theme.value === "dark"),
    toggle: () => {
      theme.value = theme.value === "dark" ? "light" : "dark";
    },
  };
}

/**
 * Applies the stored theme before the app paints. Called from `main.ts` rather
 * than from a component so the first frame is already in the right palette —
 * mounting a component first would flash the default dark theme at a light user.
 */
export const initTheme = () => apply(theme.value);
